import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  COOKIE_SETTINGS_EVENT,
  defaultCookieConsent,
  getCookieConsent,
  setCookieConsent,
} from "../utils/cookieConsent";

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [draftConsent, setDraftConsent] = useState(defaultCookieConsent);

  useEffect(() => {
    const consent = getCookieConsent();
    setVisible(!consent);
    setDraftConsent(consent || defaultCookieConsent);

    const openSettings = () => {
      setDraftConsent(getCookieConsent() || defaultCookieConsent);
      setVisible(true);
      setPreferencesOpen(true);
    };

    window.addEventListener(COOKIE_SETTINGS_EVENT, openSettings);

    return () => {
      window.removeEventListener(COOKIE_SETTINGS_EVENT, openSettings);
    };
  }, []);

  const saveChoice = ({ analytics, marketing }) => {
    const consent = setCookieConsent({ analytics, marketing });
    setDraftConsent(consent);
    setVisible(false);
    setPreferencesOpen(false);
  };

  const openPreferences = () => {
    setDraftConsent(getCookieConsent() || defaultCookieConsent);
    setVisible(true);
    setPreferencesOpen(true);
  };

  const toggleDraftConsent = (key) => {
    setDraftConsent((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const savePreferences = () => {
    saveChoice({
      analytics: draftConsent.analytics,
      marketing: draftConsent.marketing,
    });
  };

  if (!visible && !preferencesOpen) return null;

  return (
    <>
      {visible && !preferencesOpen && (
        <div className="fixed inset-x-0 bottom-0 z-40 px-3 pb-3 sm:px-5 sm:pb-5 md:px-6">
          <div className="mx-auto w-full max-w-5xl rounded-2xl border border-mmBorder bg-[#050505] p-4 shadow-2xl shadow-black/50 sm:p-5 md:p-6">
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_auto] lg:items-center lg:gap-6">
              <p className="text-sm leading-relaxed text-white sm:text-base lg:max-w-3xl">
                Mission Movement gebruikt noodzakelijke cookies om de website
                goed te laten werken. Analytics- en marketingcookies gebruiken
                we alleen met jouw toestemming.
                <Link
                  to="/legal#cookies"
                  className="mt-2 ml-1 block font-semibold text-mmAccent hover:opacity-80 md:mt-0 md:inline"
                >
                  Lees meer
                </Link>
              </p>
              <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap lg:w-auto lg:justify-end lg:gap-2">
                <button
                  type="button"
                  className="inline-flex min-h-[48px] w-full items-center justify-center rounded-xl border border-mmBorderStrong bg-transparent px-5 py-3 text-sm font-bold text-mmText transition-all hover:border-mmAccent hover:text-mmAccent focus:outline-none focus:ring-2 focus:ring-mmAccent/30 sm:w-auto"
                  onClick={() =>
                    saveChoice({ analytics: false, marketing: false })
                  }
                >
                  Alles weigeren
                </button>
                <button
                  type="button"
                  className="inline-flex min-h-[48px] w-full items-center justify-center rounded-xl border border-mmBorderStrong bg-transparent px-5 py-3 text-sm font-bold text-mmText transition-all hover:border-mmAccent hover:text-mmAccent focus:outline-none focus:ring-2 focus:ring-mmAccent/30 sm:w-auto"
                  onClick={openPreferences}
                >
                  Voorkeuren beheren
                </button>
                <button
                  type="button"
                  className="inline-flex min-h-[48px] w-full items-center justify-center rounded-xl border border-[#ffcc00] bg-[#ffcc00] px-5 py-3 text-sm font-bold text-[#050505] opacity-100 transition-all hover:border-[#e6b800] hover:bg-[#e6b800] focus:outline-none focus:ring-2 focus:ring-[#ffcc00]/40 sm:w-auto"
                  onClick={() =>
                    saveChoice({ analytics: true, marketing: true })
                  }
                  style={{
                    backgroundColor: "#ffcc00",
                    borderColor: "#ffcc00",
                    color: "#050505",
                  }}
                >
                  Alles accepteren
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {preferencesOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 px-3 py-3 sm:items-center sm:px-5">
          <div
            className="w-full max-w-xl rounded-2xl border border-mmBorder bg-[#050505] p-5 shadow-2xl shadow-black/60 sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-settings-title"
          >
            <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm mb-2">
              Cookie instellingen
            </p>
            <h2
              id="cookie-settings-title"
              className="font-display uppercase tracking-widest text-mmText text-2xl mb-3"
            >
              Jouw voorkeuren
            </h2>
            <p className="text-sm leading-relaxed text-mmTextMuted mb-5">
              Kies welke cookies Mission Movement mag gebruiken. Noodzakelijke
              cookies zijn altijd actief omdat de site anders niet goed werkt.
              Je kunt deze keuze later aanpassen via de footer.
            </p>
            <Link
              to="/legal#cookies"
              className="mb-5 inline-flex text-sm font-semibold text-mmAccent hover:opacity-80"
            >
              Lees meer
            </Link>

            <div className="space-y-3">
              <label className="flex items-center justify-between gap-4 rounded-xl border border-mmBorder bg-mmSurface px-4 py-3">
                <span>
                  <span className="block font-semibold text-mmText">
                    Noodzakelijk
                  </span>
                  <span className="block text-xs leading-relaxed text-mmTextMuted">
                    Nodig voor basisfunctionaliteit en beveiliging.
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked
                  disabled
                  className="h-5 w-5 shrink-0 accent-mmAccent disabled:opacity-70"
                />
              </label>

              <label className="flex items-center justify-between gap-4 rounded-xl border border-mmBorder bg-mmSurface px-4 py-3">
                <span>
                  <span className="block font-semibold text-mmText">
                    Analytics
                  </span>
                  <span className="block text-xs leading-relaxed text-mmTextMuted">
                    Helpt ons begrijpen hoe de website wordt gebruikt.
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={draftConsent.analytics}
                  onChange={() => toggleDraftConsent("analytics")}
                  className="h-5 w-5 shrink-0 accent-mmAccent"
                />
              </label>

              <label className="flex items-center justify-between gap-4 rounded-xl border border-mmBorder bg-mmSurface px-4 py-3">
                <span>
                  <span className="block font-semibold text-mmText">
                    Marketing
                  </span>
                  <span className="block text-xs leading-relaxed text-mmTextMuted">
                    Nodig voor advertenties, campagnemeting en retargeting.
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={draftConsent.marketing}
                  onChange={() => toggleDraftConsent("marketing")}
                  className="h-5 w-5 shrink-0 accent-mmAccent"
                />
              </label>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                className="inline-flex min-h-[48px] w-full items-center justify-center rounded-xl border border-mmBorderStrong bg-transparent px-5 py-3 text-sm font-bold text-mmText transition-all hover:border-mmAccent hover:text-mmAccent focus:outline-none focus:ring-2 focus:ring-mmAccent/30 sm:w-auto"
                onClick={() =>
                  saveChoice({ analytics: false, marketing: false })
                }
              >
                Alles weigeren
              </button>
              <button
                type="button"
                className="inline-flex min-h-[48px] w-full items-center justify-center rounded-xl border border-[#ffcc00] bg-[#ffcc00] px-5 py-3 text-sm font-bold text-[#050505] opacity-100 transition-all hover:border-[#e6b800] hover:bg-[#e6b800] focus:outline-none focus:ring-2 focus:ring-[#ffcc00]/40 sm:w-auto"
                onClick={savePreferences}
                style={{
                  backgroundColor: "#ffcc00",
                  borderColor: "#ffcc00",
                  color: "#050505",
                }}
              >
                Voorkeuren opslaan
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;
